import { useCallback, useEffect, useState } from 'react'
import { useToaster } from 'react-angle-dashboard-components'
import { useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Coords } from 'google-map-react'
import routes from '../../../routes/paths'
import { CareTabs } from '../data'
import { DirectoryQueryParam } from '../interfaces'

export const defaultCenter = {
  lat: 40.75887438182697,
  lng: -111.89457550366247
}

const { googleApiKey } = (window as any).runtimeConfig

export const useFindCareLocation = (
  selectedTab: CareTabs,
  inputText: string,
  zip: string
) => {
  const [currentLocation, setCurrentLocation] = useState<Coords | null>(null)

  const history = useHistory()
  const toastr = useToaster()

  const { data: coordsData, isLoading: isCoordsLoading } = useQuery(
    ['getCoordinates', zip],
    async () => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:${zip}|country:US&key=${googleApiKey}`
        )
        return response.json()
      } catch (error) {
        console.error(error)
        onGetCoordsError()
      }
    },
    {
      // Refetch every time the length is 5
      enabled: zip.length === 5
    }
  )

  const handleRedirect = useCallback(
    (route: string, queryParams: DirectoryQueryParam[]) => {
      history.push(
        `${route}?${[
          ...queryParams,
          { key: 'lat', value: currentLocation?.lat },
          { key: 'lng', value: currentLocation?.lng }
        ]
          .map((x) => `${x.key}=${x.value}`)
          .join('&')}`
      )
    },
    [history, currentLocation?.lat, currentLocation?.lng]
  )

  const onMore = useCallback(() => {
    const route =
      selectedTab === CareTabs.FACILITY
        ? routes.facilitiesDirectory
        : routes.providersDirectory

    handleRedirect(route, [{ key: 'query', value: inputText }])
  }, [handleRedirect, selectedTab, inputText])

  // Accurate evaluation in dependency array below
  const toasterShow = toastr.show

  const onGetCoordsError = useCallback(() => {
    toasterShow({
      message: 'Invalid Zip Code. Please try again.',
      type: 'danger',
      dimissibleIn: 5000
    })
    setCurrentLocation({
      lat: defaultCenter.lat,
      lng: defaultCenter.lng
    })
  }, [toasterShow, setCurrentLocation])

  useEffect(() => {
    if (coordsData !== undefined) {
      if (coordsData.results?.length) {
        const { lat, lng } = coordsData?.results[0]?.geometry?.location
        setCurrentLocation({ lat, lng })
      } else {
        onGetCoordsError()
      }
    }
  }, [coordsData, onGetCoordsError])

  return {
    onMore,
    handleRedirect,
    onGetCoordsError,
    coordsData,
    isCoordsLoading,
    currentLocation,
    defaultCenter
  }
}
